(() => {
  'use strict';
  const maxAgeMs = 2 * 60 * 1000;
  const prefix = 'tmm_compact_catalog:v1:';

  function readCache(key) {
    try {
      const cached = JSON.parse(sessionStorage.getItem(key) || 'null');
      return cached && Date.now() - cached.createdAt <= maxAgeMs && Array.isArray(cached.products)
        ? cached.products : null;
    } catch (_) { return null; }
  }

  function writeCache(key, products) {
    try {
      const currentVersion = key.slice(0, key.lastIndexOf(':') + 1);
      Object.keys(sessionStorage).filter(existing => existing.startsWith(prefix) && !existing.startsWith(currentVersion))
        .forEach(existing => sessionStorage.removeItem(existing));
      sessionStorage.setItem(key, JSON.stringify({ createdAt: Date.now(), products }));
    } catch (_) { /* A full session cache must not prevent shopping. */ }
  }

  function validProduct(product) {
    return product && Number.isSafeInteger(product.id) && typeof product.handle === 'string' &&
      typeof product.title === 'string' && Array.isArray(product.tags) &&
      Array.isArray(product.collections) && Array.isArray(product.images) &&
      Array.isArray(product.variants) && product.variants.length === product.variant_count;
  }

  function validatePage(data, page, first) {
    if (data.schema !== 1 || data.page !== page || !Array.isArray(data.products)) throw new Error('Invalid catalog page');
    if (!Number.isInteger(data.pages) || data.pages < 0 || data.pages > 100) throw new Error('Invalid catalog pagination');
    if (!data.products.every(validProduct)) throw new Error('Incomplete catalog product');
    if (first && (data.total !== first.total || data.pages !== first.pages || data.version !== first.version)) {
      throw new Error('Catalog changed while loading');
    }
    return data;
  }

  async function fetchPage(root, version, page, first, collection = 'all') {
    const params = new URLSearchParams({ view: 'tmm-catalog', page: String(page), v: version });
    const response = await fetch(`${root}collections/${encodeURIComponent(collection)}?${params}`);
    if (!response.ok) throw new Error(`Catalog request failed: ${response.status}`);
    return validatePage(await response.json(), page, first);
  }

  async function loadPages(root, version, expectedPages, collection) {
    if (Number.isInteger(expectedPages) && expectedPages > 0 && expectedPages <= 100) {
      const pages = await Promise.all(Array.from({ length: expectedPages },
        (_, index) => fetchPage(root, version, index + 1, null, collection)));
      pages.forEach((page, index) => validatePage(page, index + 1, pages[0]));
      return pages;
    }
    const first = await fetchPage(root, version, 1, null, collection);
    const remaining = await Promise.all(Array.from({ length: Math.max(0, first.pages - 1) },
      (_, index) => fetchPage(root, version, index + 2, first, collection)));
    return [first, ...remaining];
  }

  async function load({ root = '/', version = '', market = '', pages: expectedPages, collection = 'all' }) {
    const key = `${prefix}${market}:${version}:${collection}`;
    const cached = readCache(key);
    if (cached) return cached;
    const pages = await loadPages(root, version, expectedPages, collection);
    const first = pages[0];
    const products = pages.flatMap(page => page.products);
    if (products.length !== first.total || new Set(products.map(product => product.id)).size !== first.total) {
      throw new Error('Incomplete catalog');
    }
    writeCache(key, products);
    return products;
  }

  async function loadSources(config, sources) {
    const results = await Promise.all(sources.map(source => load({ ...config,
      collection: source.handle, pages: Math.ceil(source.count / 250)
    })));
    const products = new Map();
    results.forEach(group => group.forEach(product => {
      if (!products.has(product.id)) products.set(product.id, product);
    }));
    return [...products.values()];
  }

  function sourceType(handle) {
    if (handle.includes('-graded')) return 'graded';
    if (handle.includes('-sealed')) return 'sealed';
    return handle.includes('-singles') ? 'singles' : '';
  }

  function assertCoverage(products, expectedCollections) {
    const counts = new Map([['all', products.length]]);
    products.forEach(product => product.collections.forEach(handle => {
      if (handle !== 'all') counts.set(handle, (counts.get(handle) || 0) + 1);
    }));
    if (expectedCollections.some(({ handle, count }) => (counts.get(handle) || 0) !== count)) {
      throw new Error('Catalog does not cover the requested collections');
    }
  }

  function forCollection(products, handles, category) {
    const isAll = handles.length === 1 && handles[0] === 'all';
    const seriesPrefix = new RegExp(`^${category}-(singles|graded|sealed)-`);
    return products.flatMap(product => {
      // Keep the old collection traversal order for series links and product types.
      const memberships = isAll ? ['all'] : handles.filter(handle => product.collections.includes(handle));
      if (!memberships.length) return [];
      const types = [...new Set(memberships.map(sourceType).filter(Boolean))];
      return [{ ...product,
        images: product.images.map(image => ({ ...image, src: image.src.startsWith('//') ? `https:${image.src}` : image.src })),
        _sourceCollection: memberships.find(handle => category && seriesPrefix.test(handle)) || memberships[0],
        _sourceCollections: memberships, _sourceType: types[0] || '', _sourceTypes: types
      }];
    });
  }

  window.TMMCollectionCatalog = { load, loadSources, forCollection, assertCoverage };
})();
